import { Button, Field, FieldLabel, Input } from "@forge/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  isRedirect,
  Link,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useState } from "react";
import { trackEvent } from "../lib/analytics";
import { api, unwrap } from "../lib/api";
import { loadSession } from "../lib/auth";

export const Route = createFileRoute("/signup")({
  beforeLoad: async ({ context }) => {
    try {
      await loadSession(context.queryClient);
      throw redirect({ to: "/home" });
    } catch (error) {
      if (isRedirect(error)) throw error;
    }
  },
  component: SignupPage,
});

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "Não foi possível criar a conta.";
}

function SignupPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const signup = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.auth.register.post({
          name,
          email,
          password,
          companyName,
          acceptedTerms: true,
        }),
      ),
    onSuccess: async () => {
      trackEvent("Signup");
      queryClient.clear();
      await router.invalidate();
      await navigate({ to: "/setup" });
    },
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-xs">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link
            to="/"
            className="flex size-10 items-center justify-center rounded-surface bg-ember text-ink-inverse shadow-raised"
          >
            <Flame className="size-5" />
          </Link>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Criar conta
            </h1>
            <p className="text-2xs text-ink-muted">
              14 dias de trial para conectar sua loja
            </p>
          </div>
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            signup.mutate();
          }}
        >
          <Field>
            <FieldLabel>Seu nome</FieldLabel>
            <Input
              autoComplete="name"
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ana Silva"
            />
          </Field>
          <Field>
            <FieldLabel>E-mail</FieldLabel>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@empresa.com.br"
            />
          </Field>
          <Field>
            <FieldLabel>Senha</FieldLabel>
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
            />
          </Field>
          <Field>
            <FieldLabel>Nome da empresa</FieldLabel>
            <Input
              autoComplete="organization"
              required
              minLength={2}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Loja Exemplo"
            />
          </Field>

          <label className="flex items-start gap-2 text-2xs leading-relaxed text-ink-secondary">
            <input
              type="checkbox"
              required
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              className="mt-0.5 size-3.5 shrink-0 accent-ember"
            />
            <span>
              Li e aceito os{" "}
              <Link to="/terms" className="text-ink underline-offset-2 hover:underline">
                termos de uso
              </Link>{" "}
              e a{" "}
              <Link
                to="/privacy"
                className="text-ink underline-offset-2 hover:underline"
              >
                política de privacidade
              </Link>
              , inclusive o tratamento de dados de compradores que o
              marketplace envia à conta.
            </span>
          </label>

          {signup.isError && (
            <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
              {apiErrorMessage(signup.error)}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={!acceptedTerms}
            loading={signup.isPending}
          >
            Começar trial
          </Button>
        </form>

        <p className="mt-6 text-center text-2xs text-ink-muted">
          Já tenho conta.{" "}
          <Link to="/login" className="text-ink underline-offset-2 hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
